import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjects } from '../api/queries/projects';
import { useLookupLabel } from '../api/queries/lookups';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { EngagementLetterIngest } from '../components/ingest/EngagementLetterIngest';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { partitionByClosed } from '../utils/project';
import './ProjectsPage.css';
import { Toggle } from '../components/Toggle';

type ProjectSortKey = 'case_number' | 'client' | 'title' | 'type' | 'status' | 'priority';

export function ProjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, error } = useProjects();
  const statusLabel = useLookupLabel('project_status');
  const caseTypeLabel = useLookupLabel('case_type');
  const [showClosed, setShowClosed] = useState(false);

  const all = data?.projects ?? [];
  // Closed and archived cases are finished work; the list is a worklist. Same
  // split the pickers use (utils/project.ts), so "inactive" means one thing
  // across the app.
  const { active, closed } = partitionByClosed(all);
  const projects = showClosed ? all : active;
  const sort = useClientSort(projects, (p, key: ProjectSortKey) => {
    switch (key) {
      case 'case_number': return p.case_number;
      case 'client': return p.client_company_name ?? p.client_name ?? '';
      case 'title': return p.title;
      case 'type': return caseTypeLabel(p.case_type);
      case 'status': return statusLabel(p.status);
      case 'priority': return p.priority;
      default: return '';
    }
  }, { key: 'case_number', dir: 'asc' });

  if (isPending) {
    return <p className="projects-status">Loading projects…</p>;
  }

  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view projects."
        : 'Failed to load projects. Try again in a moment.';
    return <p className="projects-status projects-error">{msg}</p>;
  }

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Projects</h2>
        <div className="projects-header-actions">
          <span className="projects-count">
            {active.length} active
            {closed.length > 0 && <> · {closed.length} closed</>}
          </span>
          {closed.length > 0 && (
            <Toggle
              checked={showClosed}
              onChange={setShowClosed}
              label="Show inactive"
              title="Include closed and archived cases"
            />
          )}
          {can('project.write') && (
            <Link to="/projects/new" className="btn-cyan-primary">
              New project
            </Link>
          )}
        </div>
      </header>

      {can('document.generate') && can('document.write') && (
        <div className="section-body">
          <div className="eli-panel">
            <header className="eli-panel-head">
              <h3>Ingest Engagement Letter</h3>
              <span className="eli-panel-meta">
                Drop a signed letter to create a new matter from its details
              </span>
            </header>
            <EngagementLetterIngest
              mode={{ kind: 'new_project' }}
              onApplied={(res) => navigate(`/projects/${res.project_id}`)}
            />
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="section-body">
          <div className="projects-empty">
            {all.length === 0 ? (
              <>
                <p>No projects yet.</p>
                <p className="projects-empty-meta">
                  Create one with <strong>New project</strong>, or drop a signed
                  engagement letter above to open a matter from its details.
                </p>
              </>
            ) : (
              // Everything on file is finished. Without this the page would
              // read "No projects yet" over a header counting N closed ones.
              <>
                <p>No active projects.</p>
                <p className="projects-empty-meta">
                  {closed.length === 1 ? 'One case is' : `All ${closed.length} cases are`} closed —
                  turn on <strong>Show inactive</strong> to see {closed.length === 1 ? 'it' : 'them'}.
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="projects">
            <table className="projects-table">
              <thead>
                <tr>
                  <SortableTh column="case_number" label="Case #" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="client" label="Client" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="title" label="Title" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="priority" label="Priority" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((p) => (
                  <tr
                    key={p.id}
                    className="projects-row"
                    onClick={() => navigate(`/projects/${p.id}`)}
                  >
                    <td><code>{p.case_number}</code></td>
                    <td>{p.client_company_name ?? p.client_name}</td>
                    <td>{p.title}</td>
                    <td>{caseTypeLabel(p.case_type)}</td>
                    <td>
                      <span className={`status-pill status-${p.status}`}>
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td>P{p.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
